import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Switch,
  Button,
  Space,
  message,
  Spin,
  Typography,
  Divider,
  Row,
  Col,
  Select,
  Table,
  Upload,
  Modal,
  Popconfirm,
  Tag
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  UploadOutlined,
  DeleteOutlined,
  EditOutlined,
  FileOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthService from '../utils/auth';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const AddOnNoticeTemplateEdit = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [product, setProduct] = useState(null);
  const [template, setTemplate] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [editAttachmentModalVisible, setEditAttachmentModalVisible] = useState(false);
  const [editingAttachment, setEditingAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [attachmentForm] = Form.useForm();
  const [editAttachmentForm] = Form.useForm();

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

  useEffect(() => {
    fetchProductAndTemplate();
  }, [productId]);

  const fetchProductAndTemplate = async () => {
    setLoading(true);
    try {
      // 获取产品信息
      const productRes = await axios.get(`${API_BASE}/addon-products/${productId}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (productRes.data.success) {
        setProduct(productRes.data.data);
      }

      // 获取模板信息
      const templateRes = await axios.get(`${API_BASE}/addon-notice-templates/product/${productId}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (templateRes.data.success && templateRes.data.data) {
        const tpl = templateRes.data.data;
        setTemplate(tpl);
        setAttachments(tpl.attachments || []);
        form.setFieldsValue({
          meetingTime: tpl.meetingTime,
          meetingPlace: tpl.meetingPlace,
          meetingPlaceEn: tpl.meetingPlaceEn,
          hasFlightInfo: tpl.hasFlightInfo,
          flightDetails: tpl.flightDetails,
          flightDetailsEn: tpl.flightDetailsEn,
          itinerary: tpl.itinerary,
          itineraryEn: tpl.itineraryEn,
          notes: tpl.notes,
          notesEn: tpl.notesEn,
          isEnabled: tpl.isEnabled
        });
      } else {
        form.setFieldsValue({
          hasFlightInfo: false,
          isEnabled: true
        });
      }
    } catch (error) {
      console.error('获取数据失败:', error);
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const response = await axios.post(
        `${API_BASE}/addon-notice-templates/product/${productId}`,
        values,
        { headers: AuthService.getAuthHeaders() }
      );

      if (response.data.success) {
        message.success('模板保存成功');
        setTemplate(response.data.data);
      }
    } catch (error) {
      console.error('保存失败:', error);
      message.error(error.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadAttachment = async (values) => {
    if (!template) {
      message.warning('请先保存模板后再上传附件');
      return;
    }

    const formData = new FormData();
    formData.append('file', values.file.file);
    formData.append('name', values.name);
    formData.append('nameEn', values.nameEn || '');
    formData.append('attachmentType', values.attachmentType || 'other');

    setUploading(true);
    try {
      const response = await axios.post(
        `${API_BASE}/addon-notice-attachments/template/${template.id}/upload`,
        formData,
        {
          headers: {
            ...AuthService.getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        message.success('附件上传成功');
        setAttachments([...attachments, response.data.data]);
        setUploadModalVisible(false);
        attachmentForm.resetFields();
      }
    } catch (error) {
      console.error('上传失败:', error);
      message.error(error.response?.data?.message || '上传失败');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (id) => {
    try {
      const response = await axios.delete(`${API_BASE}/addon-notice-attachments/${id}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (response.data.success) {
        message.success('附件已删除');
        setAttachments(attachments.filter(a => a.id !== id));
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleEditAttachment = (attachment) => {
    setEditingAttachment(attachment);
    editAttachmentForm.setFieldsValue({
      name: attachment.name,
      nameEn: attachment.nameEn,
      attachmentType: attachment.attachmentType,
      isDownloadable: attachment.isDownloadable
    });
    setEditAttachmentModalVisible(true);
  };

  const handleUpdateAttachment = async () => {
    try {
      const values = await editAttachmentForm.validateFields();
      const response = await axios.put(
        `${API_BASE}/addon-notice-attachments/${editingAttachment.id}`,
        values,
        { headers: AuthService.getAuthHeaders() }
      );
      if (response.data.success) {
        message.success('附件信息已更新');
        setAttachments(attachments.map(a =>
          a.id === editingAttachment.id ? { ...a, ...values } : a
        ));
        setEditAttachmentModalVisible(false);
      }
    } catch (error) {
      message.error('更新失败');
    }
  };

  const attachmentColumns = [
    {
      title: '类型',
      dataIndex: 'attachmentType',
      key: 'attachmentType',
      width: 100,
      render: (type) => {
        const typeMap = {
          ticket: { color: 'blue', text: '机票' },
          hotel: { color: 'green', text: '酒店' },
          itinerary: { color: 'purple', text: '行程单' },
          other: { color: 'default', text: '其他' }
        };
        const t = typeMap[type] || typeMap.other;
        return <Tag color={t.color}>{t.text}</Tag>;
      }
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          <FilePdfOutlined style={{ color: '#ff4d4f' }} />
          <span>{name}</span>
          {record.nameEn && <Text type="secondary">({record.nameEn})</Text>}
        </Space>
      )
    },
    {
      title: '可下载',
      dataIndex: 'isDownloadable',
      key: 'isDownloadable',
      width: 80,
      render: (val) => val ? <Tag color="green">是</Tag> : <Tag>否</Tag>
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditAttachment(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个附件吗?"
            onConfirm={() => handleDeleteAttachment(record.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            返回
          </Button>
        </Space>

        <Title level={4}>
          Add-On通知书模板编辑: {product?.name || '加载中...'}
        </Title>
        {product?.nameEn && <Text type="secondary">{product.nameEn}</Text>}

        <Divider />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Card title="集合信息" size="small" style={{ marginBottom: 16 }}>
                <Form.Item name="meetingTime" label="集合时间">
                  <Input placeholder="如: 05:00" />
                </Form.Item>
                <Form.Item name="meetingPlace" label="集合地点">
                  <TextArea rows={2} placeholder="中文集合地点" />
                </Form.Item>
                <Form.Item name="meetingPlaceEn" label="集合地点（英文）">
                  <TextArea rows={2} placeholder="Meeting place in English" />
                </Form.Item>
              </Card>
            </Col>

            <Col span={12}>
              <Card title="机票信息" size="small" style={{ marginBottom: 16 }}>
                <Form.Item name="hasFlightInfo" label="包含机票信息" valuePropName="checked">
                  <Switch checkedChildren="是" unCheckedChildren="否" />
                </Form.Item>
                <Form.Item name="flightDetails" label="机票详情">
                  <TextArea rows={3} placeholder="航班信息（如航班号、起飞时间等）" />
                </Form.Item>
                <Form.Item name="flightDetailsEn" label="机票详情（英文）">
                  <TextArea rows={3} placeholder="Flight details in English" />
                </Form.Item>
              </Card>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Card title="行程安排" size="small" style={{ marginBottom: 16 }}>
                <Form.Item name="itinerary" label="行程内容">
                  <TextArea rows={6} placeholder="行程详细安排" />
                </Form.Item>
                <Form.Item name="itineraryEn" label="行程内容（英文）">
                  <TextArea rows={6} placeholder="Itinerary in English" />
                </Form.Item>
              </Card>
            </Col>

            <Col span={12}>
              <Card title="注意事项" size="small" style={{ marginBottom: 16 }}>
                <Form.Item name="notes" label="注意事项">
                  <TextArea rows={6} placeholder="特别注意事项" />
                </Form.Item>
                <Form.Item name="notesEn" label="注意事项（英文）">
                  <TextArea rows={6} placeholder="Notes in English" />
                </Form.Item>
              </Card>
            </Col>
          </Row>

          <Card title="附件管理（机票PDF、酒店预订单等）" size="small" style={{ marginBottom: 16 }}>
            <Space style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                onClick={() => setUploadModalVisible(true)}
                disabled={!template}
              >
                上传附件
              </Button>
              {!template && <Text type="warning">请先保存模板后再上传附件</Text>}
            </Space>
            <Table
              dataSource={attachments}
              columns={attachmentColumns}
              rowKey="id"
              size="small"
              pagination={false}
            />
          </Card>

          <Form.Item name="isEnabled" label="启用模板" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              htmlType="submit"
              loading={saving}
              size="large"
            >
              保存模板
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* 上传附件弹窗 */}
      <Modal
        title="上传附件"
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={null}
      >
        <Form
          form={attachmentForm}
          layout="vertical"
          onFinish={handleUploadAttachment}
        >
          <Form.Item
            name="name"
            label="显示名称"
            rules={[{ required: true, message: '请输入显示名称' }]}
          >
            <Input placeholder="如: Iguazu机票" />
          </Form.Item>

          <Form.Item name="nameEn" label="显示名称（英文）">
            <Input placeholder="如: Iguazu Ticket" />
          </Form.Item>

          <Form.Item name="attachmentType" label="附件类型" initialValue="other">
            <Select>
              <Option value="ticket">机票</Option>
              <Option value="hotel">酒店预订单</Option>
              <Option value="itinerary">行程单</Option>
              <Option value="other">其他</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="file"
            label="选择文件"
            rules={[{ required: true, message: '请选择文件' }]}
          >
            <Upload
              beforeUpload={() => false}
              maxCount={1}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            >
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={uploading}>
                上传
              </Button>
              <Button onClick={() => setUploadModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑附件弹窗 */}
      <Modal
        title="编辑附件信息"
        open={editAttachmentModalVisible}
        onCancel={() => setEditAttachmentModalVisible(false)}
        onOk={handleUpdateAttachment}
      >
        <Form form={editAttachmentForm} layout="vertical">
          <Form.Item
            name="name"
            label="显示名称"
            rules={[{ required: true, message: '请输入显示名称' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="nameEn" label="显示名称（英文）">
            <Input />
          </Form.Item>

          <Form.Item name="attachmentType" label="附件类型">
            <Select>
              <Option value="ticket">机票</Option>
              <Option value="hotel">酒店预订单</Option>
              <Option value="itinerary">行程单</Option>
              <Option value="other">其他</Option>
            </Select>
          </Form.Item>

          <Form.Item name="isDownloadable" label="允许下载" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AddOnNoticeTemplateEdit;
